import {
  type AssignmentInput,
  type ExternalProfile,
  type Invite,
  type MemberAccessMailResult,
  type MemberCard,
  type MemberClearance,
  MONEY_LEVELS,
  type MoneyLevel,
  type MoneyScopeInput,
} from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { ArrowLeft, Lock, Mail, Send, Trash2, Unlock } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { HistoryButton } from '../../components/History';
import { ViewAsButton } from '../../components/ViewAs';
import { useCrumbs } from '../../layout/crumbs';
import { ApiError, api } from '../../lib/api';
import { dateTime, relative } from '../../lib/format';
import { counterpartiesQuery } from '../../lib/projects';
import { useCan, useMe } from '../../lib/session';
import { Button } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Field, Input, Select, Textarea } from '../../ui/form';
import { Panel } from '../../ui/layout';
import { AssignmentsEditor } from './AssignmentsEditor';
import { ExternalProfileFields } from './ExternalProfileFields';
import { MemberPasswordReset } from './MemberPasswordReset';
import { ResendDialog, useRoles } from './MembersPage';
import { MoneyScopesEditor } from './MoneyScopesEditor';

/** Что видит человек на каждом уровне денег — пояснение под выбором */
const MONEY_HINTS: Record<MoneyLevel, string> = {
  none: 'суммы и цены скрыты везде',
  warehouse: 'видит цены и суммы склада: приход, выдача, остатки',
  acts: 'плюс суммы актов выполненных работ',
  acts_invoices: 'плюс счета, заявки на оплату и платёжный календарь',
  all: 'все суммы: бюджет, договоры, маржа',
};

const cardKey = (id: string) => ['admin', 'member-card', id];

function clearanceOf(e: unknown): MemberClearance | undefined {
  return (e instanceof ApiError ? (e.details as { clearance?: MemberClearance } | undefined) : undefined)
    ?.clearance;
}

/** Действие справа: кнопка и пояснение простыми словами */
function Action({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-2 border-t border-line px-5 py-4 first:border-0" aria-label={title}>
      <h3 className="text-[14px] font-medium text-ink">{title}</h3>
      {children}
    </section>
  );
}

function Hint({ children }: { children: ReactNode }) {
  return <p className="text-[13px] leading-5 text-ink-2">{children}</p>;
}

function ClearanceBox({ due }: { due: MemberClearance }) {
  return (
    <div className="rounded-md border border-warning/40 bg-warning-soft px-3 py-2.5 text-[13px] text-ink">
      <p className="font-medium">Обходной лист не закрыт — за человеком числится по складу:</p>
      <ul className="mt-1 list-disc pl-5">
        {due.lines.slice(0, 8).map((l, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: строки перечня могут совпадать
          <li key={`${i}-${l}`}>{l}</li>
        ))}
        {due.lines.length > 8 ? <li>… и ещё {due.lines.length - 8}</li> : null}
      </ul>
      <p className="mt-1 text-ink-2">
        Лучше сначала принять всё на склад. Если продолжить — долг останется за ним.
      </p>
    </div>
  );
}

/** Закрыть или открыть вход в компанию; при закрытии — причина, её видят администраторы */
function BlockDialog({ card, onClose }: { card: MemberCard; onClose: () => void }) {
  const qc = useQueryClient();
  const closing = card.member.status === 'active';
  const [reason, setReason] = useState('');
  const [due, setDue] = useState<MemberClearance | null>(null);
  const save = useMutation({
    mutationFn: (force?: boolean) =>
      api(`/admin/members/${card.member.membershipId}`, {
        method: 'PATCH',
        body: closing
          ? {
              status: 'disabled',
              statusReason: reason.trim() || null,
              ...(force ? { forceDisable: true } : {}),
            }
          : { status: 'active' },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin'] });
      toast(closing ? 'Вход закрыт' : 'Вход открыт');
      onClose();
    },
    onError: (e) => {
      const d = clearanceOf(e);
      if (d) setDue(d);
    },
  });
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={closing ? 'Закрыть вход' : 'Открыть вход'}
      description={card.member.fullName}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button
            variant={closing ? 'danger' : 'primary'}
            loading={save.isPending}
            onClick={() => save.mutate(Boolean(due))}
          >
            {closing ? (due ? 'Закрыть с долгом' : 'Закрыть вход') : 'Открыть вход'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3 text-[14px] text-ink">
        {closing ? (
          <>
            <p>
              Человек сразу перестанет работать в портале этой компании: открытые страницы попросят войти
              заново. Записи, подписи и история остаются. Открыть вход можно в любой момент.
            </p>
            <Field label="Причина" htmlFor="block-reason" hint="Её видят администраторы компании в карточке">
              <Textarea
                id="block-reason"
                rows={2}
                maxLength={500}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </Field>
            {due ? <ClearanceBox due={due} /> : null}
          </>
        ) : (
          <p>Человек снова сможет войти с прежним паролем, с теми же ролями и доступом к деньгам.</p>
        )}
        {due ? null : <ErrorBox error={save.error} />}
      </div>
    </Dialog>
  );
}

/** Убрать из компании: членство снимается, учётная запись и подписи остаются с его именем */
function RemoveDialog({ card, onClose }: { card: MemberCard; onClose: () => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [due, setDue] = useState<MemberClearance | null>(null);
  const remove = useMutation({
    mutationFn: (force?: boolean) =>
      api(`/admin/members/${card.member.membershipId}`, {
        method: 'DELETE',
        query: force ? { force: '1' } : undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin'] });
      toast(`${card.member.fullName} больше не работает в компании`);
      navigate({ to: '/admin/members' });
    },
    onError: (e) => {
      const d = clearanceOf(e);
      if (d) setDue(d);
    },
  });
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title="Удалить пользователя из компании"
      description={card.member.fullName}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button variant="danger" loading={remove.isPending} onClick={() => remove.mutate(Boolean(due))}>
            {due ? 'Удалить с долгом' : 'Удалить'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3 text-[14px] text-ink">
        <p>
          {card.member.fullName} больше не сможет войти в компанию: роли, проекты и доступ к деньгам снимутся.
          Всё, что он внёс и подписал, останется с его именем. Вернуть человека — только новым приглашением.
        </p>
        <p className="text-ink-2">
          Если человек временно не работает, лучше закрыть вход — его можно открыть одной кнопкой.
        </p>
        {due ? <ClearanceBox due={due} /> : <ErrorBox error={remove.error} />}
      </div>
    </Dialog>
  );
}

function CardForm({ card }: { card: MemberCard }) {
  const qc = useQueryClient();
  const me = useMe();
  const roles = useRoles();
  const m = card.member;
  const [fullName, setFullName] = useState(m.fullName);
  const [position, setPosition] = useState(m.position ?? '');
  const [phone, setPhone] = useState(card.phone ?? '');
  const [kind, setKind] = useState(m.kind);
  const [counterpartyId, setCounterpartyId] = useState(m.counterpartyId ?? '');
  const [externalProfile, setExternalProfile] = useState<ExternalProfile | ''>(m.externalProfile ?? '');
  const [externalProjectIds, setExternalProjectIds] = useState<string[]>(m.externalProjectIds);
  const [workerId, setWorkerId] = useState(card.worker?.id ?? '');
  const [moneyLevel, setMoneyLevel] = useState<MoneyLevel>(m.moneyLevel);
  const [assignments, setAssignments] = useState<AssignmentInput[]>(
    m.roles.map((r) => ({ roleId: r.roleId, scopeType: r.scopeType, scopeId: r.scopeId })),
  );
  const [moneyScopes, setMoneyScopes] = useState<MoneyScopeInput[]>(
    (m.moneyScopes ?? []).map(({ scopeType, scopeId, moneyLevel }) => ({ scopeType, scopeId, moneyLevel })),
  );
  const counterparties = useQuery({ ...counterpartiesQuery(), enabled: kind === 'external' });
  const moneyHidden =
    kind === 'external' && externalProfile !== 'supplier' && externalProfile !== 'subcontractor';
  const ownerEdits = Boolean(me.tenant?.isOwner) && !m.isOwner;

  // Связь с карточкой работника ставит раздел персонала по своим правилам (право на ресурсы)
  const canWorkers = useCan('resources.edit');
  const workerChanged = kind === 'staff' && canWorkers && workerId !== (card.worker?.id ?? '');
  const save = useMutation({
    mutationFn: async () => {
      await api(`/admin/members/${m.membershipId}`, {
        method: 'PATCH',
        body: {
          position: position.trim() || null,
          kind,
          assignments,
          counterpartyId: kind === 'external' && counterpartyId ? counterpartyId : null,
          externalProfile: kind === 'external' ? externalProfile || null : null,
          externalProjectIds: kind === 'external' ? externalProjectIds : [],
          ...(card.personalEditable
            ? {
                ...(fullName.trim() !== m.fullName ? { fullName: fullName.trim() } : {}),
                ...(phone.trim() !== (card.phone ?? '') ? { phone: phone.trim() || null } : {}),
              }
            : {}),
          ...(ownerEdits
            ? { moneyLevel: moneyHidden ? 'none' : moneyLevel, moneyScopes: moneyHidden ? [] : moneyScopes }
            : {}),
        },
      });
      if (workerChanged) {
        if (card.worker) await api(`/workers/${card.worker.id}`, { method: 'PATCH', body: { userId: null } });
        if (workerId) await api(`/workers/${workerId}`, { method: 'PATCH', body: { userId: m.userId } });
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin'] });
      if (workerChanged) qc.invalidateQueries({ queryKey: ['workers'] });
      if (m.userId === me.user.id) qc.invalidateQueries({ queryKey: ['me'] });
      toast('Сохранено');
    },
    // Часть правки могла сохраниться до отказа — карточка перечитывается
    onError: () => qc.invalidateQueries({ queryKey: cardKey(m.membershipId) }),
  });
  const unlinkTelegram = useMutation({
    mutationFn: () =>
      api(`/admin/members/${m.membershipId}`, { method: 'PATCH', body: { telegramChatId: null } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: cardKey(m.membershipId) });
      toast('Telegram отвязан');
    },
  });

  const externalIncomplete =
    kind === 'external' &&
    m.status === 'active' &&
    (!counterpartyId || !externalProfile || !externalProjectIds.length);

  return (
    <Panel
      title="Данные пользователя"
      actions={
        <>
          <ViewAsButton member={m} />
          <HistoryButton table="memberships" rowId={m.membershipId} title={m.fullName} />
        </>
      }
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Логин (почта)" htmlFor="c-email" hint="Им человек входит в портал">
            <Input id="c-email" value={m.email} readOnly />
          </Field>
          <Field
            label="Фамилия, имя, отчество"
            htmlFor="c-name"
            hint={card.personalLock ?? 'Так имя видно в записях, подписях и письмах'}
          >
            <Input
              id="c-name"
              value={fullName}
              disabled={!card.personalEditable}
              onChange={(e) => setFullName(e.target.value)}
            />
          </Field>
          <Field label="Должность" htmlFor="c-pos" hint="В этой компании">
            <Input id="c-pos" value={position} onChange={(e) => setPosition(e.target.value)} />
          </Field>
          <Field label="Телефон" htmlFor="c-phone">
            <Input
              id="c-phone"
              type="tel"
              value={phone}
              disabled={!card.personalEditable}
              onChange={(e) => setPhone(e.target.value)}
            />
          </Field>
          <Field label="Кто это" htmlFor="c-kind">
            <Select
              id="c-kind"
              disabled={m.isOwner}
              value={kind}
              onChange={(e) => setKind(e.target.value as 'staff' | 'external')}
            >
              <option value="staff">Сотрудник компании</option>
              <option value="external">Внешний участник: подрядчик, поставщик, заказчик</option>
            </Select>
          </Field>
          {kind === 'staff' ? (
            <Field
              label="Карточка работника в учёте"
              htmlFor="c-worker"
              hint={
                canWorkers
                  ? 'Нужна, чтобы человек вносил рабочие дни, наряды и инструктажи от своего имени'
                  : 'Связь ставит тот, кто ведёт персонал (право на ресурсы): Ресурсы → Персонал'
              }
            >
              <Select
                id="c-worker"
                value={workerId}
                disabled={!canWorkers}
                onChange={(e) => setWorkerId(e.target.value)}
              >
                <option value="">— не связан —</option>
                {card.workerOptions.map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.fullName}
                    {w.personnelNo ? `, таб. № ${w.personnelNo}` : ''}
                  </option>
                ))}
              </Select>
            </Field>
          ) : (
            <Field
              label="Организация подрядчика"
              htmlFor="c-cp"
              hint="Внешний участник видит только записи своей организации"
            >
              <Select id="c-cp" value={counterpartyId} onChange={(e) => setCounterpartyId(e.target.value)}>
                <option value="">— не привязан —</option>
                {m.counterpartyId && !(counterparties.data ?? []).some((c) => c.id === m.counterpartyId) ? (
                  <option value={m.counterpartyId}>{m.counterpartyName ?? 'текущая организация'}</option>
                ) : null}
                {(counterparties.data ?? []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Select>
            </Field>
          )}
          <div className="flex flex-col gap-2 text-[13px] text-ink-2 sm:col-span-2">
            <span className="text-[14px] font-medium text-ink">Telegram</span>
            {card.telegram.linked ? (
              <div className="flex flex-wrap items-center gap-2">
                <Badge tone="success">Telegram подключён</Badge>
                {card.telegram.username ? <span>@{card.telegram.username}</span> : null}
                {card.personalEditable ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    loading={unlinkTelegram.isPending}
                    onClick={() => unlinkTelegram.mutate()}
                  >
                    Отвязать
                  </Button>
                ) : null}
              </div>
            ) : (
              <span>Не подключён — уведомления в Telegram не приходят</span>
            )}
            <span>
              Человек подключает Telegram сам: Профиль → Уведомления → «Подключить Telegram». Так уведомления
              точно придут ему, а не в чужой чат.
            </span>
          </div>
        </div>

        {kind === 'external' ? (
          <ExternalProfileFields
            prefix="c"
            profile={externalProfile}
            onProfile={setExternalProfile}
            projectIds={externalProjectIds}
            onProjects={setExternalProjectIds}
          />
        ) : null}

        <Field
          label="Роли и объекты доступа"
          hint="Роль на всю компанию — доступ ко всем проектам; роль на проект, объект или направление — только к ним"
        >
          {m.isOwner ? (
            <p className="mb-2 rounded-md bg-surface-2 px-3 py-2 text-[13px] text-ink-2">
              Владелец компании получает все права независимо от ролей.
            </p>
          ) : null}
          {roles.data ? (
            <AssignmentsEditor roles={roles.data} value={assignments} onChange={setAssignments} />
          ) : (
            <Spinner />
          )}
        </Field>

        <Field
          label="Доступ к деньгам"
          htmlFor="c-money"
          hint={
            m.isOwner
              ? 'Владелец всегда видит все суммы'
              : moneyHidden
                ? 'Заказчик, проектировщик и надзор сумм не видят'
                : ownerEdits
                  ? `${MONEY_LEVELS[moneyLevel]}: ${MONEY_HINTS[moneyLevel]}`
                  : `${MONEY_LEVELS[m.moneyLevel]}: ${MONEY_HINTS[m.moneyLevel]}. Меняет владелец компании`
          }
        >
          <Select
            id="c-money"
            value={moneyHidden ? 'none' : moneyLevel}
            disabled={!ownerEdits || moneyHidden}
            onChange={(e) => setMoneyLevel(e.target.value as MoneyLevel)}
          >
            {Object.entries(MONEY_LEVELS).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </Select>
        </Field>
        {ownerEdits && !moneyHidden ? (
          <Field
            label="Деньги по проектам"
            hint="Сверх общего уровня: в этих проектах, направлениях и юрлицах человек видит суммы этого уровня"
          >
            <MoneyScopesEditor
              value={moneyScopes}
              onChange={setMoneyScopes}
              projectIds={kind === 'external' ? externalProjectIds : undefined}
            />
          </Field>
        ) : null}

        <ErrorBox error={save.error} />
        <div className="flex flex-wrap items-center gap-3">
          <Button type="submit" variant="primary" loading={save.isPending} disabled={externalIncomplete}>
            Сохранить
          </Button>
          {externalIncomplete ? (
            <span className="text-[13px] text-ink-2">
              Внешнему участнику с открытым входом нужны профиль, организация и проекты
            </span>
          ) : null}
        </div>
      </form>
    </Panel>
  );
}

function Actions({ card }: { card: MemberCard }) {
  const me = useMe();
  const m = card.member;
  const [blocking, setBlocking] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [resending, setResending] = useState(false);
  const accessMail = useMutation({
    mutationFn: () =>
      api<MemberAccessMailResult>(`/admin/members/${m.membershipId}/access-mail`, { method: 'POST' }),
  });
  const canResend = card.pendingInvite && (card.pendingInvite.moneyLevel === 'none' || me.tenant?.isOwner);
  const self = m.userId === me.user.id;

  return (
    <Panel title="Действия" bodyClassName="p-0">
      {/* «Выдать новый пароль»: ссылка для нового пароля на почту человека (ДС-19) */}
      <div className="px-5 py-4">
        <MemberPasswordReset member={m} />
      </div>

      <Action title="Отправить приглашение">
        {card.pendingInvite ? (
          <>
            <Hint>
              Приглашение ещё не принято{' '}
              {new Date(card.pendingInvite.expiresAt).getTime() > Date.now()
                ? `(действует до ${dateTime(card.pendingInvite.expiresAt)})`
                : '(срок вышел)'}
              . Отправить заново — новая ссылка на 7 дней, прежняя перестанет работать.
            </Hint>
            {canResend ? (
              <Button size="md" icon={<Send className="size-4" />} onClick={() => setResending(true)}>
                Отправить приглашение
              </Button>
            ) : (
              <Hint>С доступом к деньгам — отправить заново может владелец компании.</Hint>
            )}
          </>
        ) : (
          <Hint>Человек уже принял приглашение. Напомнить, как войти, — письмом с доступом ниже.</Hint>
        )}
      </Action>

      <Action title="Письмо с доступом">
        <Hint>
          Адрес портала, логин, роли и инструкция «С чего начать». Пароля в письме нет: если человек его не
          помнит — «Выдать новый пароль».
        </Hint>
        {me.tenant?.isDemo ? (
          <Hint>В демо-компании письма не отправляются.</Hint>
        ) : accessMail.data ? (
          <p className="rounded-md bg-success-soft px-3 py-2.5 text-[14px] text-ink" role="status">
            {accessMail.data.reviewed
              ? 'Письмо ушло на проверку: в компании включена проверка писем перед отправкой.'
              : `Письмо отправлено на ${accessMail.data.email}.`}
          </p>
        ) : (
          <Button
            size="md"
            icon={<Mail className="size-4" />}
            disabled={m.status !== 'active'}
            loading={accessMail.isPending}
            onClick={() => accessMail.mutate()}
          >
            Отправить письмо с доступом
          </Button>
        )}
        <ErrorBox error={accessMail.error} />
      </Action>

      <Action title="Подтверждение входа">
        <ul className="flex flex-col gap-1 text-[13px] text-ink">
          <li>Пароль: {card.login.hasPassword ? 'задан' : 'не задан — человек ещё не входил'}</li>
          <li>
            Вход по ключу на устройстве (отпечаток, лицо, PIN):{' '}
            {card.login.passkeys ? `да, ключей — ${card.login.passkeys}` : 'нет'}
          </li>
          <li>Последний вход: {card.login.lastLoginAt ? relative(card.login.lastLoginAt) : 'не входил'}</li>
        </ul>
        <Hint>
          Вход с нового устройства или из другой страны попадает в журнал безопасности. Ключи человек
          добавляет сам в профиле.
        </Hint>
      </Action>

      <Action title={m.status === 'active' ? 'Заблокировать вход' : 'Вход закрыт'}>
        {m.status === 'active' ? (
          <Hint>
            Человек сразу перестанет работать в компании, данные и подписи останутся. Открыть вход можно в
            любой момент.
          </Hint>
        ) : (
          <Hint>
            {card.disabled?.reason ? `Причина: ${card.disabled.reason}. ` : ''}
            {card.disabled?.at ? `Закрыт ${dateTime(card.disabled.at)}` : 'Закрыт'}
            {card.disabled?.byName ? `, закрыл ${card.disabled.byName}` : ''}.
          </Hint>
        )}
        {m.isOwner || self ? (
          <Hint>{m.isOwner ? 'Вход владельца компании не закрывается.' : 'Свой вход закрыть нельзя.'}</Hint>
        ) : (
          <Button
            size="md"
            variant={m.status === 'active' ? 'danger' : 'primary'}
            icon={m.status === 'active' ? <Lock className="size-4" /> : <Unlock className="size-4" />}
            onClick={() => setBlocking(true)}
          >
            {m.status === 'active' ? 'Заблокировать вход' : 'Открыть вход'}
          </Button>
        )}
      </Action>

      <Action title="Удалить пользователя">
        <Hint>
          Человек уходит из компании: роли и доступ снимаются. Данные, записи и подписи остаются с его именем.
        </Hint>
        {card.removeLock ? (
          <Hint>{card.removeLock}</Hint>
        ) : (
          <Button
            size="md"
            variant="danger"
            icon={<Trash2 className="size-4" />}
            onClick={() => setRemoving(true)}
          >
            Удалить пользователя
          </Button>
        )}
      </Action>

      {blocking ? <BlockDialog card={card} onClose={() => setBlocking(false)} /> : null}
      {removing ? <RemoveDialog card={card} onClose={() => setRemoving(false)} /> : null}
      {resending && card.pendingInvite ? (
        <ResendDialog
          invite={
            { id: card.pendingInvite.id, email: m.email, fullName: m.fullName } as Pick<
              Invite,
              'id' | 'email' | 'fullName'
            > as Invite
          }
          onClose={() => setResending(false)}
        />
      ) : null}
    </Panel>
  );
}

/** Карточка пользователя в администрировании: данные слева, действия справа (на телефоне — ниже) */
export function MemberCardPage() {
  const { memberId } = useParams({ strict: false }) as { memberId: string };
  const card = useQuery({
    queryKey: cardKey(memberId),
    queryFn: () => api<MemberCard>(`/admin/members/${memberId}`),
    // Форма заполнена из карточки: перечитывать при возврате на вкладку — значит стереть начатую правку
    refetchOnWindowFocus: false,
  });
  const m = card.data?.member;
  useCrumbs(m ? [{ label: 'Сотрудники', to: '/admin/members' }, { label: m.fullName }] : null, card.error);

  return (
    <div className="flex flex-col gap-4">
      <Link
        to="/admin/members"
        className="inline-flex h-11 w-fit items-center gap-1.5 text-[14px] text-ink-2 hover:text-ink"
      >
        <ArrowLeft className="size-4" aria-hidden />
        Все сотрудники
      </Link>
      {card.isLoading ? (
        <Spinner />
      ) : card.error || !card.data || !m ? (
        <ErrorBox error={card.error} />
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-[20px] leading-7 font-semibold [overflow-wrap:anywhere] text-ink">
              {m.fullName}
            </h2>
            {m.isOwner ? <Badge tone="ink">Владелец компании</Badge> : null}
            {m.status === 'active' ? (
              <Badge tone="success">Вход разрешён</Badge>
            ) : (
              <Badge tone="danger">Вход закрыт</Badge>
            )}
            {m.kind === 'external' ? <Badge>Внешний участник</Badge> : null}
          </div>
          <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_340px]">
            {/* После сохранения форма берёт то, что записал сервер */}
            <CardForm key={`${memberId}-${card.dataUpdatedAt}`} card={card.data} />
            <Actions card={card.data} />
          </div>
        </>
      )}
    </div>
  );
}
